import { useEffect, useState } from "react";
import type { SessionVideoStatusView } from "@fiket/booth-schema";
import { fetchSessionVideo } from "./sessionApi";
import { SESSION_VIDEO_POLL_MAX, SESSION_VIDEO_POLL_MS, isVideoPollDone } from "./videoDelivery";
import { trackError } from "../../telemetry/activity";

export interface SessionVideoPoll {
  /** 마지막으로 받은 서버 상태. 첫 응답 전 null */
  remote: SessionVideoStatusView | null;
  /** 마지막 폴링 실패 여부 — 화면 판정은 remote 로 하고 이 값은 로그용 */
  pollError: boolean;
}

/**
 * `GET /api/sessions/:code/video` 를 5초 간격으로 폴링 (BOOTH-60 지연 전달). 제작 중이 아니게 되면(ready/failed/none) 멈춘다.
 * `shortCode` 가 없거나 `enabled` 가 false 면 돌지 않는다. 이탈 시 AbortController 로 진행 중 fetch·sleep 을 함께 취소 — `usePrintJob` 과 같은 패턴.
 */
export function useSessionVideo(shortCode: string | null, enabled: boolean): SessionVideoPoll {
  const [remote, setRemote] = useState<SessionVideoStatusView | null>(null);
  const [pollError, setPollError] = useState(false);

  useEffect(() => {
    setRemote(null);
    setPollError(false);
    if (!shortCode || !enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      for (let i = 0; i < SESSION_VIDEO_POLL_MAX && !signal.aborted; i++) {
        try {
          const view = await fetchSessionVideo(shortCode, signal);
          setRemote(view);
          setPollError(false);
          if (isVideoPollDone(view)) return;
        } catch (err) {
          if (signal.aborted) return;
          console.warn("[PrintShare] 영상 상태 조회 실패 — 다음 폴링에서 재시도", err);
          trackError("video_poll_failed", err);
          setPollError(true);
        }
        try {
          await sleep(SESSION_VIDEO_POLL_MS, signal);
        } catch {
          return; // abort
        }
      }
    })();
    return () => controller.abort();
  }, [shortCode, enabled]);

  return { remote, pollError };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve(), ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true }
    );
  });
}
